import { get } from 'lodash';
import webRTCBus from '@core/mixins/bus/webrtc';
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

import WEB_RTC_ACTIONS from '@core/utils/webrtc';

const NO_ANSWER_NOTIFICATION_AUDIO = require('@/assets/sounds/hangup.mp3')
const END_CALL_NOTIFICATION_AUDIO = require('@/assets/sounds/end_call_2.mp3')
const CALLING_NOTIFICATION_AUDIO = require('@/assets/sounds/calling.mp3')
const CALL_DROPPED_NOTIFICATION_AUDIO = require('@/assets/sounds/end_call.mp3')

export default {
  data() {
    return {
      socket: null,
      localStream: null,
      peerConnection: null,

      callTimeoutId: null,
      patientDisconnectedIntervalId: null,

      callAccepted: false,

      callingTone: new Audio(CALLING_NOTIFICATION_AUDIO),
      callDroppedTone: new Audio(CALL_DROPPED_NOTIFICATION_AUDIO),
      bufferedICE: [],
      config: {
        iceServers: [
          {
            urls: 'stun:stun.l.google.com:19302',
          },
          {
            urls: 'stun:stun1.l.google.com:19302',
          },
          {
            urls: 'stun:stun2.l.google.com:19302',
          },
        ],
      },

      bufferedCallEvents: []
    };
  },
  computed: {
    myVideoStream() {
      return this.$store.getters[`webrtc/localStream`]
    },
    remoteVideoStream() {
      return this.$store.getters[`webrtc/remoteStream`]
    }
  },
  sockets: {
    connect() {
      console.log("[SOCKET CONNECTED FROM MIXIN]")
      // setTimeout(() => {
      //   console.log("HIT SERVER")
      //   this.$socket.client.emit('peer-reconnected')
      // }, 1e3);
    },
  },
  created() {
    this.callingTone.loop = true;
    this.callDroppedTone.loop = false;
  },
  mounted() {
    this.initSocketAndWebRTC();
    this.initWebRTCBus()
  },
  methods: {
    initSocketAndWebRTC() {
      console.log("INIT SOCKET IO EVENTS")
      this.peerConnection = new RTCPeerConnection(this.config);

      // handle Local ICE candidates
      this.peerConnection.onicecandidate = (event) => {
        console.log("GENERATE LOCAL ICE", event?.candidate)
        if (event.candidate) {
          console.log("SEND ICE")
          this.$socket.client.emit('ice-candidate', event.candidate);
        }
      };

      // handle remote stream
      this.peerConnection.ontrack = (event) => {
        console.log('REMOTE STREAM PINGED')
        if (event.streams && event.streams[0]) {
          const remoteStream = event.streams[0];
          console.log('ADD REMOTE STREAM', remoteStream)
          this.$store.commit(`webrtc/SET_REMOTE_STREAM`, remoteStream);
          if (this.bufferedCallEvents.length) {
            // used to send call logs when patient reconnect after call dropped
            console.log("LOAD CALL ACTIVITY", this.bufferedCallEvents)
            let delay = 0;
            this.bufferedCallEvents.forEach(activity => {
              delay += 5e2;
              setTimeout(() => {
                console.log('SEND ======>')
                this.sendCallEvent(activity)
              }, delay);
            });

            // reset
            this.bufferedCallEvents = [];
          }
        }
      };

      // handle remote ICE Candidates
      this.$socket.client.on(WEB_RTC_ACTIONS.ICE_CANDIDATE, async (candidate) => {
        try {
          console.log('REMOTE ICE RECEIVED', candidate)
          this.clearPatientDisconnectedInterval();
          if (this.callAccepted) {
            await this.addIceCandidateToConnection(candidate)
          } else {
            // buffer remote stream;
            console.log('BUFFER ICE', candidate)
            this.bufferedICE.push(candidate);
          }
        } catch (error) {
          console.log('ADDING REMOTE ICE ERROR', error)
        }
      })

      this.$socket.client.on(WEB_RTC_ACTIONS.CALL_ACCEPTED, this.handlePatientAcceptedCall);

      this.$socket.client.on(WEB_RTC_ACTIONS.RE_NEGOTIATE_OFFER, this.onRenegotiateOffer);
      // this.$socket.client.on(WEB_RTC_ACTIONS.PEER_RECONNECTED, this.onPatientReconnected);
      this.$socket.client.on(WEB_RTC_ACTIONS.PEER_DISCONNECTED, this.onPatientDisconnected);

      this.$socket.client.on(WEB_RTC_ACTIONS.END_CALL, () => {
        console.log('END CALL RECEIVED')
        this.endCall(false);

        this.$toast({
          component: ToastificationContent,
          props: {
            variant: 'success',
            title: 'Call Ended!',
            icon: 'CheckCircleIcon',
            text: "The call has ended."
          },
        });
      });

      this.$socket.client.on(WEB_RTC_ACTIONS.CALL_REJECTED, () => {
        if (this.callTimeoutId) {
          clearTimeout(this.callTimeoutId);
          this.callTimeoutId = null;
        }

        this.stopCall();

        this.$toast({
          component: ToastificationContent,
          props: {
            variant: 'warning',
            title: 'Call Rejected!',
            icon: 'CheckCircleIcon',
            text: "Patient rejected call."
          },
        });
      })

      this.$socket.client.on(WEB_RTC_ACTIONS.LOG_CALL_EVENT, (event) => {
        this.$store.commit(`webrtc/SET_REMOTE_CALL_EVENT`, event)
      });
    },
    initWebRTCBus() {
      webRTCBus.$on(WEB_RTC_ACTIONS.INITIATE_CALL, this.tryToInitiateCall);
      webRTCBus.$on(WEB_RTC_ACTIONS.DIAL_CALL, this.dialPatientCall);

      webRTCBus.$on(WEB_RTC_ACTIONS.END_CALL, this.endCall);
      webRTCBus.$on(WEB_RTC_ACTIONS.CALL_DROPPED, this.dropCall);
      webRTCBus.$on(WEB_RTC_ACTIONS.LOG_CALL_EVENT, this.sendCallEvent);
      webRTCBus.$on(WEB_RTC_ACTIONS.TOGGLE_AUDIO, this.toggleAudioStream);
      webRTCBus.$on(WEB_RTC_ACTIONS.TOGGLE_VIDEO, this.toggleVideoStream);
    },
    tryToInitiateCall({ consultation, patient }) {
      try {
        console.log("Consult -> ", consultation)
        this.$store.commit(`webrtc/SET_CALLEE`, patient);
        this.showOutgoingVideoCallModal({ consultation, patient });
      } catch (error) {
        const error_msg = error.message;

        this.$toast({
          component: ToastificationContent,
          timeout: 5000,
          props: {
            variant: 'danger',
            title: 'Call Failed!',
            icon: 'CheckCircleIcon',
            text: error_msg || "Call initiation failed"
          },
        });
      }
    },
    seekNotificationPermissionIfNotAlreadyGranted() {
      if (Notification.permission === "default") {
        Notification.requestPermission().then(permission => {
          console.log("NOTIFICATION PERMISSION", permission)
        })
      }
    },
    playDialingTone() {
      this.callingTone.play().catch(error => {
        console.log('Audio playback failed', error)
      })
    },
    stopRingtone() {
      this.callingTone.pause();
      this.callingTone.currentTime = 0; // Reset to the beginning
    },
    playEndCallSound() {
      const audio = new Audio(END_CALL_NOTIFICATION_AUDIO)
      audio.play()
    },
    playCallDroppedSound() {
      const audio = new Audio(CALL_DROPPED_NOTIFICATION_AUDIO)
      audio.play()
    },
    playNoCallAnswerSound() {
      const audio = new Audio(NO_ANSWER_NOTIFICATION_AUDIO)
      audio.play()
    },
    hideCameraPermissionModal() {
      this.$modal.hide("camera-permission-modal")
    },
    showCameraPermissionModal() {
      this.$modal.show("camera-permission-modal")
    },
    showOutgoingVideoCallModal(params) {
      this.$modal.show("outgoing-call-modal", params)
    },
    hideOutgoingVideoCallModal() {
      this.$modal.hide("outgoing-call-modal")
    },
    hideVideoCallModal() {
      this.$modal.hide("video-modal")
    },
    showVideoCallModal() {
      this.$modal.show("video-modal")
    },
    closeCamera() {
      console.log('CLOSE CAMERA', this.localStream);
      if (this.localStream) {
        const tracks = this.localStream.getTracks();

        tracks.forEach((track) => {
          track.stop();
        });

        this.localStream = null;
      }
    },
    sendCallEvent(payload) {
      this.bufferedCallEvents.push(payload)
      this.$socket.client.emit(WEB_RTC_ACTIONS.LOG_CALL_EVENT, payload)
    },
    clearPatientDisconnectedInterval() {
      if (this.patientDisconnectedIntervalId) {
        clearInterval(this.patientDisconnectedIntervalId)
        this.patientDisconnectedIntervalId = false;
      }
    },
    stopCall() {
      this.stopRingtone();
      this.closeCamera()
      this.hideOutgoingVideoCallModal();
      this.hideVideoCallModal()
      this.clearPatientDisconnectedInterval();

      this.callAccepted = false;
      this.bufferedCallEvents = [];

      this.$store.commit(`webrtc/SET_CALLEE`, null);
      this.$store.commit(`webrtc/SET_LOCAL_STREAM`, null);
      this.$store.commit(`webrtc/SET_REMOTE_STREAM`, null);
    },
    dropCall() {
      if (this.callTimeoutId) {
        clearTimeout(this.callTimeoutId);
        this.callTimeoutId = null;
      }

      this.stopCall();
      this.playCallDroppedSound();
      this.$socket.client.emit(WEB_RTC_ACTIONS.CALL_DROPPED);
    },
    noCallAnswer() {
      this.stopCall();
      this.playNoCallAnswerSound();
      this.$socket.client.emit(WEB_RTC_ACTIONS.NO_ANSWER);
    },
    endCall(notify_patient = true) {
      this.stopCall()
      this.playEndCallSound();

      if (notify_patient) {
        this.$socket.client.emit(WEB_RTC_ACTIONS.END_CALL, {
          receiver: 'patient',
        });
      }
    },
    getVideoElement(identifier) {
      return document.getElementById(identifier)
    },
    addVideoStream(video, stream) {
      if (video) {
        video.srcObject = stream
        video.addEventListener('loadedmetadata', () => {
          video.play()
        })
      }
    },
    toggleVideoStream(enabled) {
      const stream = this.myVideoStream
      if (stream) {
        stream.getVideoTracks().forEach(track => { track.enabled = enabled });
        this.$store.commit(`webrtc/SET_LOCAL_STREAM`, stream);
      }
    },
    toggleAudioStream(enabled) {
      const stream = this.myVideoStream
      if (stream) {
        stream.getAudioTracks().forEach(track => { track.enabled = enabled });
        this.$store.commit(`webrtc/SET_LOCAL_STREAM`, stream);
      }
    },
    handleStreamTermination(stream, target) {
      const tracks = stream.getTracks();

      tracks.forEach(track => {
        track.addEventListener('ended', () => {
          console.log('A track in the stream ended');
          if (stream.getTracks().every(t => t.readyState === 'ended')) {
            console.log('The entire stream has ended');
          }

          const targetDom = document.getElementById(target);
          if (targetDom) {
            targetDom.remove()
          }
        });
      });
    },
    onPatientDisconnected() {
      if (!this.callAccepted) return;
      console.log('PATIENT DISCONNECTED NOTICE RECEIVED')
      this.clearPatientDisconnectedInterval();

      const playNotice = () => {
        this.$toast({
          component: ToastificationContent,
          timeout: 2e3,
          props: {
            variant: 'warning',
            title: 'Patient Disconnected',
            icon: 'CheckCircleIcon',
            text: "Patient is disconnected, retrying to connect..."
          },
        });
      }

      playNotice()

      this.patientDisconnectedIntervalId = setInterval(() => {
        playNotice()
      }, 5e3);
    },
    async onRenegotiateOffer(call_id) {
      console.log('RE-NEGOTIATE OFFER RECEIVED', call_id)
      if (!this.callAccepted) return;

      // TODO maybe verify call_id

      try {
        const offer = await this.peerConnection.createOffer({ iceRestart: true });
        await this.peerConnection.setLocalDescription(offer);

        this.$socket.client.emit(WEB_RTC_ACTIONS.RE_NEGOTIATE_OFFER, offer);
      } catch (error) {
        const error_msg = error.message;
        console.log("RE-NEGOTIATE OFFER ERROR", error_msg)
      }
    },
    async addIceCandidateToConnection(candidate) {
      await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
    },
    async requestCameraAccess() {
      try {
        // when this fails, we want to be able to show relevant error
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: false
        });

        this.localStream = stream;
        this.$store.commit(`webrtc/SET_LOCAL_STREAM`, stream);

        // add stream to track 
        stream.getTracks().forEach(track => this.peerConnection.addTrack(track, stream));
      } catch (error) {
        console.log(error.message, error.name)
        this.showCameraPermissionModal()
        if (error.message === "Permission denied") {
          // Use error.name for modern browsers
          // this.permissionError = true;
          console.log("Permission denied");
          throw new Error('Camera permission denied. Enable permission to proceed');
          // show permission denied error notification
        } else {
          console.error("Unspecified Error:", error);
          throw new Error('An unspecified error occurred')
        }
      }
    },
    async dialPatientCall(id) {
      try {
        // add local stream to peer connection
        await this.requestCameraAccess();
        this.playDialingTone()

        this.callTimeoutId = setTimeout(() => {
          this.noCallAnswer();

          this.$toast({
            component: ToastificationContent,
            timeout: 5000,
            props: {
              variant: 'warning',
              title: 'Call Failed!',
              icon: 'CheckCircleIcon',
              text: "Call dropped or no answer"
            },
          });
        }, 30e3);

        // create and send offer
        const offer = await this.peerConnection.createOffer({ iceRestart: true });
        await this.peerConnection.setLocalDescription(offer);

        const response = await this.useJwt().mainService.calls.dial(id, {
          offer
        });

        const call_info = get(response, 'data.data');

        // TODO remove this when VoiP Push is implemented
        this.$socket.client.emit(WEB_RTC_ACTIONS.DIAL_PATIENT, call_info.id);
      } catch (error) {
        console.log("dial patient error", error);
        // drop call as it failed.
        this.dropCall();
      }
    },
    async handlePatientAcceptedCall(answer) {
      console.log('CALL ACCEPTED')
      this.stopRingtone();
      this.hideOutgoingVideoCallModal()

      if (this.callTimeoutId) {
        clearTimeout(this.callTimeoutId);
        this.callTimeoutId = null;
      }

      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
      this.callAccepted = true;

      this.showVideoCallModal();
    },
  },
  beforeDestroy() {
    console.log('WebRTC Mixin Tear down...')
    this.peerConnection?.close();
    this.peerConnection = null;

    // teardown socket listeners
    this.$socket.client.off(WEB_RTC_ACTIONS.END_CALL);
    this.$socket.client.off(WEB_RTC_ACTIONS.LOG_CALL_EVENT);
    this.$socket.client.off(WEB_RTC_ACTIONS.CALL_ACCEPTED);
    this.$socket.client.off(WEB_RTC_ACTIONS.CALL_REJECTED);
    this.$socket.client.off(WEB_RTC_ACTIONS.ICE_CANDIDATE);

    // teardown webRTCBus listeners 
    webRTCBus.$off(WEB_RTC_ACTIONS.END_CALL)
    webRTCBus.$off(WEB_RTC_ACTIONS.CALL_DROPPED)
    webRTCBus.$off(WEB_RTC_ACTIONS.LOG_CALL_EVENT)
    webRTCBus.$off(WEB_RTC_ACTIONS.INITIATE_CALL)
    webRTCBus.$off(WEB_RTC_ACTIONS.TOGGLE_AUDIO)
    webRTCBus.$off(WEB_RTC_ACTIONS.TOGGLE_VIDEO)
  }
}
